/**
 * The search view's state lives in the URL (`/search?q&mode&scope&f.*`, spec §5).
 * These helpers are the only place that translates between the query string
 * and the typed state, so the view, the app-bar field and the tests agree on
 * what is written, what is a default, and what is silently dropped.
 */
import { readValues, writeValues } from "../../shared/lib/urlParams";
import type { SearchFacetState } from "./facets";

export type SearchMode = "keyword" | "semantic";
export type SearchScope = "all" | "containers" | "items" | "photos";

export const DEFAULT_SEARCH_MODE: SearchMode = "keyword";
export const DEFAULT_SEARCH_SCOPE: SearchScope = "all";

const SEARCH_MODES: readonly SearchMode[] = ["keyword", "semantic"];
const SEARCH_SCOPES: readonly SearchScope[] = ["all", "containers", "items", "photos"];

export interface SearchState {
  q: string;
  mode: SearchMode;
  scope: SearchScope;
  /** Property filters, repeated `f.owner` / `f.tag` / `f.type` params. */
  facets: SearchFacetState;
}

/**
 * Read the typed search state from a query string.
 *
 * @param params the current URL search params.
 * @returns the state, with defaults for absent or unrecognised values.
 */
export function readSearchState(params: URLSearchParams): SearchState {
  const mode = params.get("mode");
  const scope = params.get("scope");
  return {
    q: params.get("q") ?? "",
    mode: isSearchMode(mode) ? mode : DEFAULT_SEARCH_MODE,
    scope: isSearchScope(scope) ? scope : DEFAULT_SEARCH_SCOPE,
    facets: {
      owners: readValues(params, "f.owner"),
      tags: readValues(params, "f.tag").filter(Boolean),
      types: readValues(params, "f.type").filter(Boolean),
    },
  };
}

/**
 * Write the typed search state onto a copy of the current params: `q` whenever
 * it is non-empty, `mode`/`scope` only when they differ from their defaults.
 * Unrelated params (the dev inspector flag) survive.
 *
 * @param current the params to start from.
 * @param state the state to write.
 * @returns a new params object; the input is not mutated.
 */
export function applySearchState(current: URLSearchParams, state: SearchState): URLSearchParams {
  const next = new URLSearchParams(current);
  writeNonEmpty(next, "q", state.q);
  writeNonDefault(next, "mode", state.mode, DEFAULT_SEARCH_MODE);
  writeNonDefault(next, "scope", state.scope, DEFAULT_SEARCH_SCOPE);
  writeValues(next, "f.owner", state.facets.owners);
  writeValues(next, "f.tag", state.facets.tags);
  writeValues(next, "f.type", state.facets.types);
  return next;
}

/**
 * Build the `/search` path for a set of params.
 *
 * @param params the search params, already containing only what should appear.
 * @returns `/search` or `/search?...`.
 */
export function searchUrl(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `/search?${query}` : "/search";
}

function isSearchMode(value: string | null): value is SearchMode {
  return value !== null && (SEARCH_MODES as readonly string[]).includes(value);
}

function isSearchScope(value: string | null): value is SearchScope {
  return value !== null && (SEARCH_SCOPES as readonly string[]).includes(value);
}

function writeNonEmpty(params: URLSearchParams, key: string, value: string): void {
  if (value) params.set(key, value);
  else params.delete(key);
}

function writeNonDefault<T extends string>(
  params: URLSearchParams,
  key: string,
  value: T,
  fallback: T,
): void {
  if (value === fallback) params.delete(key);
  else params.set(key, value);
}
